import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useI18n } from "@/lib/i18n.jsx";
import { motion } from "framer-motion";
import { Dumbbell, PlayCircle, Clock } from "lucide-react";
import api from "@/lib/api";
import Spinner from "@/components/Spinner";
import { WorkoutsComingSoon } from "@/components/workouts/WorkoutsComingSoon";
import { fmtMinutes } from "@/components/workouts/workoutMeta";

function CourseCard({ c, i, onOpen }) {
  const pct = c.video_count ? Math.round((c.completed_count / c.video_count) * 100) : 0;
  return (
    <motion.button type="button" onClick={onOpen} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
      className="w-full text-start bg-white rounded-3xl border border-neutral-100 shadow-card overflow-hidden active:scale-[0.99] transition-transform"
      data-testid={`workout-course-${c.id}`}>
      <div className="relative aspect-[16/9] bg-neutral-100">
        {c.cover_url
          ? <img src={c.cover_url} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
          : <div className="absolute inset-0 orange-gradient flex items-center justify-center"><Dumbbell className="w-12 h-12 text-white/80" /></div>}
        <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/0 to-black/0" />
        <span className="absolute bottom-3 start-3 inline-flex items-center gap-1 bg-black/55 backdrop-blur text-white text-[11px] font-bold px-2.5 py-1 rounded-full">
          <PlayCircle className="w-3.5 h-3.5" /> {c.section_count > 0 ? `${c.section_count} أقسام · ` : ""}{c.video_count} درس
        </span>
        {c.total_sec > 0 && (
          <span className="absolute bottom-3 end-3 inline-flex items-center gap-1 bg-black/55 backdrop-blur text-white text-[11px] font-bold px-2.5 py-1 rounded-full">
            <Clock className="w-3.5 h-3.5" /> {fmtMinutes(c.total_sec)}
          </span>
        )}
      </div>
      <div className="p-4">
        <div className="font-black text-neutral-900 text-base" data-testid={`workout-course-title-${c.id}`}>{c.title}</div>
        {c.description && <p className="text-xs text-neutral-500 mt-1 line-clamp-2">{c.description}</p>}
        {c.completed_count > 0 && (
          <div className="mt-3">
            <div className="h-1.5 rounded-full bg-neutral-100 overflow-hidden"><div className="h-full orange-gradient rounded-full" style={{ width: `${pct}%` }} /></div>
            <div className="text-[11px] text-neutral-500 mt-1 num">{c.completed_count} من {c.video_count} تمارين مكتملة</div>
          </div>
        )}
      </div>
    </motion.button>
  );
}

export default function WorkoutsPage() {
  const nav = useNavigate();
  const { lang } = useI18n();
  const [access, setAccess] = useState(null);
  const [courses, setCourses] = useState(null);

  useEffect(() => {
    api.get("/workouts/access").then(({ data }) => {
      setAccess(data);
      if (data.enabled) api.get("/workouts/courses").then((r) => setCourses(r.data)).catch(() => setCourses([]));
    }).catch(() => setAccess({ enabled: false, waitlisted: false }));
  }, []);

  if (lang === "en") return <Navigate to="/app" replace />;
  if (!access) return <div className="flex justify-center pt-24"><Spinner /></div>;
  if (!access.enabled) return <WorkoutsComingSoon waitlisted={access.waitlisted} />;

  return (
    <div className="px-4 sm:px-6 pt-6 pb-32 max-w-3xl mx-auto lg:mx-0 space-y-4" dir="rtl" data-testid="workouts-page">
      <div className="space-y-1">
        <h1 className="text-2xl sm:text-3xl font-display font-semibold leading-tight" data-testid="workouts-title">التمارين</h1>
        <p className="text-muted-foreground text-xs sm:text-sm">اختر البرنامج المناسب وابدأ تمرينك بفيديوهات واضحة.</p>
      </div>
      {courses === null && <div className="flex justify-center pt-10"><Spinner /></div>}
      {courses?.length === 0 && (
        <div className="text-center text-sm text-neutral-500 py-16" data-testid="workouts-empty">لا توجد برامج بعد — أضفها من لوحة التحكم.</div>
      )}
      <div className="grid sm:grid-cols-2 gap-4">
        {courses?.map((c, i) => <CourseCard key={c.id} c={c} i={i} onOpen={() => nav(`/app/workouts/${c.id}`)} />)}
      </div>
    </div>
  );
}
